import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MoreHorizontal, Search, Printer } from "lucide-react";
import { StatusPill } from "@/pages/SalesHub";
import Logo from "@/components/brand/Logo";

const INV_STATUS = {
    draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
    sent: "bg-blue-50 text-blue-700 border-blue-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
};
function InvStatus({ s }) {
    return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${INV_STATUS[s] || INV_STATUS.draft}`}>{s}</span>;
}

export function Billing() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [payFor, setPayFor] = useState(null);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/invoices", { params: q ? { q } : {} }); setItems(data.items || []); setSummary(data.summary); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]);

    const doAction = async (id, path, msg) => {
        try { await api.post(`/invoices/${id}/${path}`); toast.success(msg); load(); }
        catch (err) {
            const d = err?.response?.data?.detail;
            if (d && typeof d === "object" && d.shortages) toast.error(`Insufficient stock for ${d.shortages.map(s => s.sku).join(", ")}`);
            else toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="page-billing">
            <PageHeader title="Billing" description="Invoicing, credit terms, and receivables." />
            <div className="p-8 space-y-4 max-w-[1400px]">
                {summary && (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        {[
                            { k: "total", label: "Total invoices", v: summary.total },
                            { k: "outstanding", label: "Outstanding", v: `₹${Number(summary.outstanding).toLocaleString("en-IN")}` },
                            { k: "paid", label: "Collected", v: `₹${Number(summary.paid).toLocaleString("en-IN")}` },
                            { k: "overdue", label: "Overdue", v: summary.overdue, tone: summary.overdue > 0 ? "red" : "" },
                        ].map(c => (
                            <div key={c.k} data-testid={`bill-summary-${c.k}`} className={`rounded-md border bg-white px-4 py-3 ${c.tone === "red" ? "border-red-200" : "border-zinc-200"}`}>
                                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{c.label}</div>
                                <div className="mt-1 font-display text-xl font-semibold">{c.v}</div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" strokeWidth={1.5} />
                        <Input placeholder="Search invoices…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="bill-search" />
                    </div>
                    <div className="ml-auto text-xs text-zinc-500 font-mono">Create invoices from Sales Hub</div>
                </div>
                <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                    <Table>
                        <TableHeader><TableRow className="bg-zinc-50/60">
                            {["Invoice", "Customer", "Total", "Paid", "Status", "Issued", ""].map(h => <TableHead key={h} className={`text-[10px] uppercase tracking-widest text-zinc-500 ${["Total", "Paid"].includes(h) ? "text-right" : ""}`}>{h}</TableHead>)}
                        </TableRow></TableHeader>
                        <TableBody>
                            {loading ? <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-zinc-500">Loading…</TableCell></TableRow>
                            : items.length === 0 ? <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-zinc-500">No invoices yet. Create one from Sales Hub.</TableCell></TableRow>
                            : items.map(i => (
                                <TableRow key={i.id} data-testid={`bill-row-${i.id}`}>
                                    <TableCell><span className="font-mono text-xs">{i.number}</span></TableCell>
                                    <TableCell><div className="text-sm font-medium">{i.customer_name}</div></TableCell>
                                    <TableCell className="text-right font-mono text-xs">₹{Number(i.grand_total).toLocaleString("en-IN")}</TableCell>
                                    <TableCell className="text-right font-mono text-xs text-zinc-500">₹{Number(i.amount_paid || 0).toLocaleString("en-IN")}</TableCell>
                                    <TableCell><InvStatus s={i.status} /></TableCell>
                                    <TableCell className="text-xs text-zinc-500 font-mono">{new Date(i.issue_date).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`bill-actions-${i.id}`}><MoreHorizontal className="h-4 w-4" strokeWidth={1.5} /></Button></DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => navigate(`/billing/${i.id}/print`)} data-testid={`bill-print-${i.id}`}><Printer className="mr-2 h-3 w-3" strokeWidth={1.5} />Print / view</DropdownMenuItem>
                                                {i.status === "draft" && <DropdownMenuItem onClick={() => doAction(i.id, "send", "Sent — stock deducted")} data-testid={`bill-send-${i.id}`}>Send (deduct stock)</DropdownMenuItem>}
                                                {(i.status === "sent" || i.status === "draft") && <DropdownMenuItem onClick={() => setPayFor(i)}>Record payment</DropdownMenuItem>}
                                                {(i.status === "draft" || i.status === "sent") && <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => doAction(i.id, "cancel", "Cancelled")}>Cancel</DropdownMenuItem>}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
            <PayDialog invoice={payFor} onClose={() => setPayFor(null)} onDone={load} />
        </div>
    );
}

function PayDialog({ invoice, onClose, onDone }) {
    const [amt, setAmt] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (invoice) setAmt(String((invoice.grand_total - (invoice.amount_paid || 0)).toFixed(2))); }, [invoice]);
    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try { await api.post(`/invoices/${invoice.id}/mark-paid`, { amount: Number(amt) }); toast.success("Payment recorded"); onDone(); onClose(); }
        catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
    };
    return (
        <Dialog open={!!invoice} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="bill-pay-dialog">
                <DialogHeader><DialogTitle className="font-display">Record payment</DialogTitle><DialogDescription>{invoice?.number} · {invoice?.customer_name}</DialogDescription></DialogHeader>
                {invoice && (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs font-mono space-y-1">
                            <div className="flex justify-between"><span className="text-zinc-500">Total</span><span>₹{Number(invoice.grand_total).toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-zinc-500">Already paid</span><span>₹{Number(invoice.amount_paid || 0).toFixed(2)}</span></div>
                        </div>
                        <div className="space-y-1.5"><Label className="text-xs">Amount</Label><Input type="number" step="any" required value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="bill-pay-amount" /></div>
                        <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} data-testid="bill-pay-submit">{saving ? "Saving…" : "Record"}</Button></DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

export function InvoicePrint() {
    const { id } = useParams();
    const [inv, setInv] = useState(null);
    useEffect(() => { api.get(`/invoices/${id}`).then(r => setInv(r.data.item)).catch(() => {}); }, [id]);
    if (!inv) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
    return (
        <div className="min-h-screen bg-white" data-testid="invoice-print">
            <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>
            <div className="no-print border-b border-zinc-200 bg-white sticky top-0 z-10">
                <div className="mx-auto max-w-4xl flex items-center justify-between px-8 py-3">
                    <Button variant="outline" size="sm" onClick={() => window.history.back()}>Back</Button>
                    <Button size="sm" onClick={() => window.print()} data-testid="print-btn"><Printer className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />Print</Button>
                </div>
            </div>
            <div className="mx-auto max-w-4xl px-12 py-12 font-mono">
                <div className="flex items-start justify-between border-b border-zinc-200 pb-6">
                    <div>
                        <div className="flex items-center gap-2"><Logo size={32} /><div><div className="font-display text-lg font-semibold">Yug Traders</div><div className="text-[11px] text-zinc-500">GSTIN · address · phone</div></div></div>
                    </div>
                    <div className="text-right"><div className="font-display text-2xl font-semibold">Invoice</div><div className="text-xs text-zinc-500">{inv.number}</div><div className="mt-2 text-xs"><InvStatus s={inv.status} /></div></div>
                </div>
                <div className="grid grid-cols-2 gap-8 py-6 text-xs">
                    <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Bill to</div><div className="font-medium text-sm">{inv.customer_name}</div></div>
                    <div className="text-right space-y-1"><div><span className="text-zinc-500">Issued: </span>{new Date(inv.issue_date).toLocaleDateString()}</div>{inv.sales_order_number && <div><span className="text-zinc-500">Order: </span>{inv.sales_order_number}</div>}</div>
                </div>
                <table className="w-full text-xs border-t border-zinc-200">
                    <thead><tr className="border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500"><th className="py-2 text-left">Item</th><th className="text-right">Qty</th><th className="text-right">Price</th><th className="text-right">Tax</th><th className="text-right">Total</th></tr></thead>
                    <tbody>
                        {inv.items.map((l, i) => (
                            <tr key={l.sku || `line-${i}`} className="border-b border-zinc-100"><td className="py-2"><div>{l.name}</div><div className="text-[10px] text-zinc-500">{l.sku}</div></td><td className="text-right">{l.quantity} {l.unit}</td><td className="text-right">₹{Number(l.unit_price).toFixed(2)}</td><td className="text-right">{l.tax_rate}%</td><td className="text-right">₹{Number(l.total).toFixed(2)}</td></tr>
                        ))}
                    </tbody>
                </table>
                <div className="ml-auto mt-6 w-64 space-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>₹{Number(inv.subtotal).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Tax</span><span>₹{Number(inv.tax_total).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Discount</span><span>−₹{Number(inv.discount).toFixed(2)}</span></div>
                    <div className="flex justify-between border-t border-zinc-300 pt-2 font-display text-base font-semibold"><span>Total</span><span>₹{Number(inv.grand_total).toFixed(2)}</span></div>
                    <div className="flex justify-between text-emerald-700"><span>Paid</span><span>₹{Number(inv.amount_paid || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between font-semibold"><span>Balance due</span><span>₹{(inv.grand_total - (inv.amount_paid || 0)).toFixed(2)}</span></div>
                </div>
                {inv.notes && <div className="mt-8 border-t border-zinc-200 pt-4 text-xs text-zinc-600">{inv.notes}</div>}
                <div className="mt-16 border-t border-zinc-200 pt-4 text-center text-[10px] text-zinc-500 uppercase tracking-widest">Generated by YugOS</div>
            </div>
        </div>
    );
}

export default Billing;
